import { LadderIcon, LeafIcon, LightningIcon } from '@phosphor-icons/react';
import type { TemplateDifficulty } from '@/components/publish-flow/types';
import type { TemplateSubmissionStatus } from '@/types/templateSubmissionTypes';

// Full-strength surfaces, never tints: these pills sit over hero images, so the fill has to be opaque and saturated
// enough to own its own space. Text is the theme's dark on every bright surface and its light or the bright ink on the
// deep ones. Exported so every status or metadata pill picks its surface here rather than tinting its own.
export const PILL_SURFACE = {
  turquoise: 'bg-lureo-aqua border-lureo-aqua text-lureo-emphasis-strong',
  sky: 'bg-lureo-sky border-lureo-sky text-lureo-emphasis-strong',
  coral: 'bg-lureo-coral border-lureo-coral text-lureo-emphasis-strong',
  warmPurple: 'bg-lureo-lavender border-lureo-lavender text-lureo-emphasis-strong',
  navy: 'bg-lureo-emphasis border-lureo-brand-ink text-lureo-mint',
  // Production's berry in light; turned light in dark, where a berry fill would vanish into the card
  berry: 'bg-lureo-berry-ink border-lureo-berry-ink text-lureo-on-berry-ink',
  brand: 'bg-lureo-brand border-lureo-brand text-lureo-on-brand',
  danger: 'bg-lureo-danger border-lureo-danger text-lureo-on-emphasis',
  outline: 'bg-lureo-surface border-lureo-tag-line text-lureo-berry-ink',
  grey: 'bg-lureo-line-strong border-lureo-line-strong text-lureo-brand-ink',
  muted: 'bg-lureo-neutral border-lureo-neutral text-lureo-ink-subtle',
} as const;
const SURFACE = PILL_SURFACE;

export const PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-lureo-radius-tag border-lureo-tag-line-width px-2.5 py-1 text-lureo-12 font-semibold leading-4';

// Sized in px, not rem: the app scales its root font size by breakpoint (14/16/18px),
// and at 18px a rem-sized dot lands on 6.75px, where the browser quantises
// border-radius and the circle renders as a squircle.
export const PILL_DOT = 'h-[6px] w-[6px] shrink-0 rounded-full bg-current opacity-70';

export const STORY_STATUS_PILL = {
  draft: SURFACE.outline,
  live: SURFACE.turquoise,
  pending: SURFACE.sky,
  template: SURFACE.navy,
  archived: SURFACE.berry,
  locked: SURFACE.grey,
} as const;

export const SUBMISSION_PILL: Record<TemplateSubmissionStatus, string> = {
  draft: SURFACE.outline,
  submitted: SURFACE.sky,
  accepted: SURFACE.turquoise,
  rejected: SURFACE.coral,
};

export const DIFFICULTY_PILL: Record<TemplateDifficulty, string> = {
  beginner: SURFACE.turquoise,
  intermediate: SURFACE.sky,
  advanced: SURFACE.warmPurple,
};

export const DIFFICULTY_ICON: Record<TemplateDifficulty, typeof LeafIcon> = {
  beginner: LeafIcon,
  intermediate: LadderIcon,
  advanced: LightningIcon,
};

export const CATEGORY_PILL = SURFACE.berry;

export const TAG_PILL = SURFACE.outline;
